/// <reference types="vite/client" />
import { getAccessToken, refreshAccessToken } from './auth-api';
import * as T from './api-types';

const API_BASE = import.meta.env.VITE_API_URL || '';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function request<T>(url: string, options?: RequestInit & { responseType?: 'blob' }): Promise<T> {
  const headers: Record<string, string> = getAuthHeaders();
  if (options?.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  let res = await fetch(`${API_BASE}${url}`, { ...options, credentials: 'include', headers: { ...headers, ...options?.headers } });

  // If 401, try a token refresh and retry once
  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const retryHeaders = getAuthHeaders();
      if (options?.body && !(options.body instanceof FormData)) {
        retryHeaders['Content-Type'] = 'application/json';
      }
      res = await fetch(`${API_BASE}${url}`, { ...options, credentials: 'include', headers: { ...retryHeaders, ...options?.headers } });
    }
  }

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      message = body.error || body.message || message;
    } catch {}
    throw new ApiError(res.status, message);
  }

  if (options?.responseType === 'blob') {
    return (await res.blob()) as T;
  }

  return res.json() as T;
}

export const api = {
  dashboard: {
    get: (entrepriseId: string) =>
      request<T.DashboardStats>(`/api/dashboard?entreprise_id=${entrepriseId}`),
    getTva: (entrepriseId: string) =>
      request<T.TVADashboardResult>(`/api/dashboard/tva?entreprise_id=${entrepriseId}`),
    getMonthly: (entrepriseId: string) =>
      request<T.MonthlyTrend[]>(`/api/dashboard/monthly?entreprise_id=${entrepriseId}`),
    getAlerts: (entrepriseId: string) =>
      request<T.DashboardAlerts>(`/api/dashboard/alerts?entreprise_id=${entrepriseId}`),
    getFiscalDeadlines: () =>
      request<{ deadlines: T.FiscalDeadline[] }>(`/api/dashboard/fiscal-deadlines`),
  },

  transactions: {
    list: (entrepriseId: string, filters: Record<string, string>) => {
      const params = new URLSearchParams({ entreprise_id: entrepriseId, ...filters });
      return request<{ total: number; items: T.Transaction[]; skip: number; take: number }>(`/api/transactions?${params.toString()}`);
    },
    create: (payload: T.CreateTransactionPayload) =>
      request<T.TransactionCreateResponse>('/api/transactions', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    patch: (id: string, data: Partial<T.CreateTransactionPayload>) =>
      request<T.TransactionUpdateResponse>(`/api/transactions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (id: string, entrepriseId: string) =>
      request<{ success: true }>(`/api/transactions/${id}?entreprise_id=${entrepriseId}`, {
        method: 'DELETE',
      }),
    batchValidate: (ids: string[], entrepriseId: string) =>
      request<{ updated: number }>('/api/transactions/batch-validate', {
        method: 'POST',
        body: JSON.stringify({ ids, entreprise_id: entrepriseId }),
      }),
  },

  categories: {
    list: () => request<T.Category[]>('/api/categories'),
  },

  planComptes: {
    list: (params?: { search?: string; classe?: string | number }) => {
      const p = new URLSearchParams();
      if (params?.search) p.append('search', params.search);
      if (params?.classe) p.append('classe', String(params.classe));
      return request<T.PlanCompte[]>(`/api/plan-comptes?${p.toString()}`);
    },
    grandLivre: (numero: string, entrepriseId: string) =>
      request<T.GrandLivreEntry[]>(`/api/plan-comptes/${numero}/grand-livre?entreprise_id=${entrepriseId}`),
  },

  balance: {
    get: (entrepriseId: string, exerciceId: string) =>
      request<T.BalanceReport>(`/api/balance?entreprise_id=${entrepriseId}&exercice_id=${exerciceId}`),
  },

  journal: {
    get: (exerciceId: string, params?: { page?: number; page_size?: number }) => {
      const sp = new URLSearchParams({ exercice_id: exerciceId });
      if (params?.page) sp.append('page', params.page.toString());
      if (params?.page_size) sp.append('page_size', params.page_size.toString());
      return request<{ entries: T.JournalEntry[]; total: number; page: number; page_size: number; total_pages: number }>('/api/journal?' + sp.toString());
    },
  },

  liasse: {
    check: (entrepriseId: string) =>
      request<T.LiasseCheckResult>('/api/liasse/check', {
        method: 'POST',
        body: JSON.stringify({ entreprise_id: entrepriseId }),
      }),
    get2033: (entrepriseId: string) =>
      `${API_BASE}/api/liasse/2033?entreprise_id=${entrepriseId}&format=pdf`,
    get2031: (entrepriseId: string) =>
      `${API_BASE}/api/liasse/2031?entreprise_id=${entrepriseId}&format=pdf`,
  },

  ca12: {
    get: (entrepriseId: string) =>
      request<Blob>(`/api/ca12?entreprise_id=${entrepriseId}`, { responseType: 'blob' }),
  },

  fec: {
    get: (exerciceId: string) =>
      request<Blob>(`/api/fec?exercice_id=${exerciceId}`, { responseType: 'blob' }),
  },

  justificatifs: {
    upload: (file: File, entrepriseId: string) => {
      const form = new FormData();
      form.append('file', file);
      return request<T.JustificatifUploadResponse>(`/api/justificatifs?entreprise_id=${entrepriseId}`, { method: 'POST', body: form });
    },
    parse: (id: string, entrepriseId: string) =>
      request<T.JustificatifParseResult>(`/api/justificatifs/${id}/parse?entreprise_id=${entrepriseId}`),
    getPreviewUrl: (id: string, entrepriseId: string) =>
      request<{ url: string; filename: string }>(`/api/justificatifs/${id}/preview?entreprise_id=${entrepriseId}`),
    delete: (id: string, entrepriseId: string) =>
      request<{ success: boolean }>(`/api/justificatifs/${id}?entreprise_id=${entrepriseId}`, { method: 'DELETE' }),
  },

  autoCatRules: {
    list: (entrepriseId: string) =>
      request<{ rules: T.AutoCatRule[] }>(`/api/auto-cat-rules?entreprise_id=${entrepriseId}`),
    create: (data: { pattern: string; compte_pcg: string; taux_tva?: number; priority?: number }) =>
      request<T.AutoCatRule>('/api/auto-cat-rules', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: Partial<{ pattern: string; compte_pcg: string; taux_tva?: number; priority?: number; is_active?: boolean }>) =>
      request<T.AutoCatRule>(`/api/auto-cat-rules/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ success: true }>(`/api/auto-cat-rules/${id}`, { method: 'DELETE' }),
  },

  reconciliation: {
    match: (lines: Array<{ date: string; libelle: string; amount_centimes: number }>) =>
      request<T.MatchResponse>('/api/reconciliation/match', {
        method: 'POST',
        body: JSON.stringify({ lines }),
      }),
    importCsv: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request<T.ReconciliationImportResult>('/api/reconciliation/import', { method: 'POST', body: form });
    },
  },

  recurring: {
    list: () => request<T.RecurringTemplate[]>('/api/recurring-templates'),
    create: (data: Omit<T.RecurringTemplate, 'id' | 'created_at' | 'updated_at'>) =>
      request<T.RecurringTemplate>('/api/recurring-templates', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    generate: (id: string, exerciceId: string) =>
      request<T.TransactionCreateResponse>(`/api/recurring-templates/${id}/generate`, {
        method: 'POST',
        body: JSON.stringify({ exercice_id: exerciceId }),
      }),
  },

  cashflow: {
    getForecast: (entrepriseId: string, months: number = 3) =>
      request<T.CashFlowForecast[]>(`/api/cashflow/forecast?entreprise_id=${entrepriseId}&months=${months}`),
  },

  trialBalance: {
    get: (entrepriseId: string, exerciceId: string) =>
      request<T.TrialBalanceReport>(`/api/trial-balance?entreprise_id=${entrepriseId}&exercice_id=${exerciceId}`),
  },

  audit: {
    list: (entrepriseId: string) =>
      request<T.AuditLog[]>(`/api/audit?entreprise_id=${entrepriseId}`),
  },

  exercices: {
    list: (entrepriseId: string) =>
      request<T.ExerciceSummary[]>(`/api/exercices?entreprise_id=${entrepriseId}`),
    cloturePreview: (entrepriseId: string, exerciceId: string) =>
      request<T.CloturePreview>(`/api/exercice/cloturer/preview?entreprise_id=${entrepriseId}&exercice_id=${exerciceId}`),
    cloture: (entrepriseId: string, exerciceId: string) =>
      request<T.ClotureResult>('/api/exercice/cloturer', {
        method: 'POST',
        body: JSON.stringify({ entreprise_id: entrepriseId, exercice_id: exerciceId }),
      }),
  },

  bankSync: {
    status: (entrepriseId: string) =>
      request<T.BankAccountStatus[]>(`/api/bank-sync/status?entreprise_id=${entrepriseId}`),
    connect: (data: { entreprise_id: string; compte_id: string; provider: 'bridge' | 'budgetinsight'; access_token: string }) =>
      request<T.BankAccountStatus>('/api/bank-sync/connect', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    sync: (compteId: string, data: { entreprise_id: string; exercice_id: string }) =>
      request<T.SyncResult>(`/api/bank-sync/sync/${compteId}`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  bankImport: {
    importCsv: (entrepriseId: string, exerciceId: string, csvContent: string) =>
      request<T.ImportResult>('/api/import/csv', {
        method: 'POST',
        body: JSON.stringify({ entreprise_id: entrepriseId, exercice_id: exerciceId, csv_content: csvContent }),
      }),
  },

  importReleve: {
    parse: (entrepriseId: string, exerciceId: string, file: File, mode: 'pdfreader' | 'rawpdf' = 'pdfreader') => {
      const form = new FormData();
      form.append('entreprise_id', entrepriseId);
      form.append('exercice_id', exerciceId);
      form.append('mode', mode);
      form.append('file', file);
      return request<T.ReleveParseResult>('/api/import/releve-bancaire', { method: 'POST', body: form });
    },
    confirm: (data: T.ConfirmImportInput) =>
      request<T.ImportConfirmResponse>('/api/import/confirm', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    history: (entrepriseId: string) =>
      request<T.ImportReleveHistoryEntry[]>(`/api/import/releve-bancaire/history?entreprise_id=${entrepriseId}`),
    checkDuplicates: (entrepriseId: string, rows: { date: string; ttc_centimes: number; libelle: string }[]) =>
      request<{ duplicates: number[] }>('/api/import/check-duplicates', {
        method: 'POST',
        body: JSON.stringify({ entreprise_id: entrepriseId, rows }),
      }),
  },

  clients: {
    list: (entrepriseId: string, search?: string) => {
      const p = new URLSearchParams({ entreprise_id: entrepriseId });
      if (search) p.append('search', search);
      return request<T.Client[]>(`/api/clients?${p.toString()}`);
    },
    create: (data: { entreprise_id: string; nom: string; email?: string; siret?: string; adresse?: any }) =>
      request<T.Client>('/api/clients', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Partial<{ nom: string; email?: string; siret?: string; adresse?: any }>) =>
      request<T.Client>(`/api/clients/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string, entrepriseId: string) =>
      request<{ success: true }>(`/api/clients/${id}?entreprise_id=${entrepriseId}`, { method: 'DELETE' }),
  },

  fournisseurs: {
    list: (entrepriseId: string, search?: string) => {
      const p = new URLSearchParams({ entreprise_id: entrepriseId });
      if (search) p.append('search', search);
      return request<T.Fournisseur[]>(`/api/fournisseurs?${p.toString()}`);
    },
    create: (data: { entreprise_id: string; nom: string; email?: string; siret?: string; telephone?: string }) =>
      request<T.Fournisseur>('/api/fournisseurs', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Partial<{ nom: string; email?: string; siret?: string; telephone?: string; entreprise_id: string }>) =>
      request<T.Fournisseur>(`/api/fournisseurs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: string, entrepriseId: string) =>
      request<{ deleted: true }>(`/api/fournisseurs/${id}?entreprise_id=${entrepriseId}`, { method: 'DELETE' }),
  },

  documents: {
    list: (entrepriseId: string) =>
      request<T.Document[]>(`/api/documents?entreprise_id=${entrepriseId}`),
    create: (data: T.DocumentCreateData) =>
      request<T.Document>('/api/documents', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: Partial<Omit<T.Document, 'id' | 'created_at' | 'updated_at' | 'client'>>) =>
      request<T.Document>(`/api/documents/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string, entrepriseId: string) =>
      request<{ success: true }>(`/api/documents/${id}?entreprise_id=${entrepriseId}`, { method: 'DELETE' }),
    convertToFacture: (id: string, entrepriseId: string) =>
      request<{ facture_id: string; facture_number: string }>(`/api/documents/${id}/convert`, {
        method: 'POST',
        body: JSON.stringify({ entreprise_id: entrepriseId }),
      }),
    emit: (id: string, entrepriseId: string, exerciceId: string) =>
      request<Blob>(`/api/documents/${id}/emit`, {
        method: 'POST',
        body: JSON.stringify({ entreprise_id: entrepriseId, exercice_id: exerciceId }),
        responseType: 'blob',
      }),
    getPdf: (id: string, entrepriseId: string) =>
      `${API_BASE}/api/documents/${id}/pdf?entreprise_id=${entrepriseId}`,
  },

  comparison: {
    get: (entrepriseId: string, exerciceA: string, exerciceB: string) =>
      request<T.ComparisonReport>(`/api/reports/comparison?entreprise_id=${entrepriseId}&exercice_a=${exerciceA}&exercice_b=${exerciceB}`),
  },
};
